import { EpgDetailDataSource } from './EpgDetailDataSource';
import { EpgApi } from '../../../api/EpgApi';
import { EpgUtil, ISortedProgram, ISortedSeasons } from '../../utility/EpgUtil';
import { AssetType } from '../../../api/models/Shared.types';
import { SharedFavouriteActionButton } from '../../../pages/detail/actionRowButtons/SharedFavouriteActionButton';
import { EpgSeriesDetailDescriptionDataSource } from './SeriesDetailDescriptionDataSource';
import { atvPromiseAll } from '../../utility/PromiseUtil';
import { DetailTranslationKeys, SettingsKeys } from '../../../api/models/TranslationKeys';
import { EpgEpiRibbonData, TabbedEpgEpisodesRibbonDataSource } from '../../cms/TabbedEpgEpisodesRibbonDataSource';
import { BookmarkUtility } from '../../utility/BookmarkUtility';
import { EpgWatchAction } from '../actions/EpgWatchAction';
import { ActionButton } from '../../../pages/detail/actionRowButtons/ActionButton';
import { StaticPageUrls } from '../../../api/analytics/ClientEvents';
import { Backend } from '../../../api/Backend';
import {
  IBookmarkElementV1,
  IProgramSummary,
  IScheduleDetail,
  ISeasonSummary,
  ISeries,
} from '../../../api/models/AndromeTV.types';
import { Recording } from '../../../api/user/RecordingApi.types';
import { ChannelModel } from '../../../api/EpgApi.types';
import { IActionButtonDefinition, IDetailModel } from '../detail.types';

export class SeriesDetailDataSource extends EpgDetailDataSource {
  private schedules: IScheduleDetail[] | undefined;
  private programs: IProgramSummary[] | undefined;
  private seasons: ISeasonSummary[] | undefined;
  private seriesId: string | undefined;

  constructor(backend: Backend) {
    super(backend);
    this.series = undefined;
    this.schedules = undefined;
    this.programs = undefined;
  }

  override logPageView() {
    this.backend.logService.trackPageView(StaticPageUrls.epgSeriesDetail);
  }

  override load(seriesId: string): Promise<IDetailModel | undefined> {
    this.seriesId = seriesId;
    const fromuntil = EpgUtil.epgFromUntil(this.backend);
    const promiseArr: Promise<ISeries | void>[] = [];
    if (this.backend.deviceProfile) {
      promiseArr.push(
        EpgApi.series(
          this.backend,
          seriesId,
          fromuntil.from,
          fromuntil.until,
          this.backend.deviceProfile.profile.catalog
        )
      );
    }
    promiseArr.push(this.initChannels());
    return Promise.all(promiseArr).then(([asset]) => {
      //todo check adult
      if (asset) {
        this.series = asset.series;
        this.schedules = asset.schedules;
        this.programs = asset.programs?.sort((a, b) => {
          if (!a.episode || !b.episode) return 0;
          if (a.episode > b.episode) {
            return 1;
          } else if (a.episode < b.episode) {
            return -1;
          } else {
            return 0;
          }
        });
        this.seasons = asset.seasons;
        return this.getDetailData();
      }
      return undefined;
    });
  }

  getDetailData(): Promise<IDetailModel | undefined> {
    return atvPromiseAll([this.getRibbons(), this.getNextEpisode()]).then(([ribbons, nextEpisodeInfo]) => {
      if (this.series) {
        const res: IDetailModel = {
          description: new EpgSeriesDetailDescriptionDataSource(this.seriesId, this.series, this.backend),
          images: {
            detailPoster: this.series?.posterImage,
          },
          buttons: this.getButtons(nextEpisodeInfo),
          ribbonDatasources: ribbons,
        };
        return res;
      }
    });
  }

  override getRibbons() {
    const id = this.series ? this.series.id : this.program?.id;
    const type = this.series ? AssetType.SERIES : AssetType.PROGRAM;

    let result: any[] = [];

    const data: EpgEpiRibbonData = {
      series: this.series,
      seasons: this.seasons,
      programs: this.programs,
      schedules: this.schedules,
    };

    if (this.series && this.series.id && this.channelsList) {
      const episodesRibbon = TabbedEpgEpisodesRibbonDataSource.fromData(
        this.backend,
        this.series.id,
        this.backend.translations.forKey(DetailTranslationKeys.detail_btn_all_episodes),
        this.channelsList,
        data
      );
      result.push(episodesRibbon);
    }
    if (id) result = result.concat(this.createRelatedDataSources(this.backend, type, id, true));

    return this.getRecordingRibbon().then(rib => {
      if (rib) result.splice(0, 0, rib);
      return result;
    });
  }

  override getButtons(nextEpisodeInfo: ISortedProgram): IActionButtonDefinition[] {
    const result: IActionButtonDefinition[] = [];

    if (
      nextEpisodeInfo &&
      this.backend.globalSettings.boolValue(SettingsKeys.showSeriesWatchButton) &&
      this.series &&
      this.channelsList
    ) {
      const actionHandler = new EpgWatchAction(
        this.backend,
        AssetType.SERIES,
        new ChannelModel(nextEpisodeInfo.channel),
        nextEpisodeInfo.schedule,
        nextEpisodeInfo.program,
        this.series,
        nextEpisodeInfo.season,
        undefined,
        nextEpisodeInfo.program.adult,
        this.channelsList,
        true
      );

      result.push({
        type: ActionButton,
        actionHandler,
        label: this.backend.translations.forKey(DetailTranslationKeys.detail_btn_watch_now),
      });
    }

    if (this.backend.isAuthorized()) {
      result.push({
        type: SharedFavouriteActionButton,
        assetId: this.series?.id,
        assetType: AssetType.SERIES,
        assetIsAdult: this.series?.adult,
      });
    }

    return result;
  }

  getNextEpisode(): Promise<ISortedProgram | undefined> {
    let showSeriesWatchButton = this.backend.globalSettings.boolValue(SettingsKeys.showSeriesWatchButton);
    showSeriesWatchButton = showSeriesWatchButton ? showSeriesWatchButton : false;
    if (!showSeriesWatchButton) {
      return Promise.resolve(undefined);
    }
    if (!this.backend.isAuthorized()) return Promise.resolve(undefined);

    if (!this.seriesId) return Promise.resolve(undefined);
    return atvPromiseAll([
      this.backend.cachedBookmarkApi.seriesBookmarks(this.backend, this.seriesId, AssetType.PROGRAM),
      this.backend.cachedRecordingsApi.recordingsForSeries(this.backend, this.seriesId),
    ]).then(([bookmarks, recordings]) => {
      const sortedPrograms = EpgUtil.sortProgramsPerSeason(
        this.backend,
        this.seasons,
        this.programs,
        this.schedules,
        this.channelsList,
        this.series
      );

      let nextProgramForSeason = undefined;

      // try to find next epi from the last bookmark
      if (bookmarks && bookmarks.length > 0) {
        const latestBookmark = BookmarkUtility.getLatestBookmark(bookmarks);

        if (latestBookmark && this.programs) {
          nextProgramForSeason = this.nextEpisodeFromBookmark(
            latestBookmark,
            sortedPrograms,
            this.programs,
            recordings
          );
        }
      }

      // if none from bookmark, take the first episode of the series
      if (!nextProgramForSeason) {
        nextProgramForSeason = this.getFirstEpiOfFirstSeason(sortedPrograms, recordings);
      }

      return nextProgramForSeason;
    });
  }

  nextEpisodeFromBookmark(
    bookmark: IBookmarkElementV1,
    sortedProgramsPerSeason: ISortedSeasons[],
    programs: IProgramSummary[],
    recordings: Recording[]
  ): ISortedProgram | undefined {
    const progIndex = programs.findIndex(prog => prog.id === bookmark.id);

    if (progIndex >= 0) {
      const lastProgram = programs[progIndex];
      const sortedSeason = sortedProgramsPerSeason.find(season => season.seasonId === lastProgram?.season);
      if (BookmarkUtility.isFullyWatched(this.backend, bookmark)) {
        // If already last episode → no watch button
        if (progIndex < programs.length - 1 && lastProgram && sortedSeason) {
          return this.getNextProgram(lastProgram, sortedSeason, recordings);
        }
      } else {
        const tempNextProgram = sortedSeason?.sortedPrograms.find(
          sortedProgram => sortedProgram.program.id === lastProgram?.id
        );
        if (
          tempNextProgram &&
          (EpgUtil.canWatchReplay(tempNextProgram.schedule, new ChannelModel(tempNextProgram.channel)) ||
            this.hasWatchableRecording(tempNextProgram, recordings))
        ) {
          return tempNextProgram;
        }
      }
    }
    return undefined;
  }

  getNextProgram(
    lastProgram: IProgramSummary,
    sortedSeason: ISortedSeasons,
    recordings: Recording[]
  ): ISortedProgram | undefined {
    let selectedNextProgram;

    if (sortedSeason) {
      const lastProgramIndex = sortedSeason.sortedPrograms.findIndex(
        sortedProgram => sortedProgram.program.id === lastProgram.id
      );

      //  IF LAST PROGRAM HAS AN EPISODE NUMBER
      if (lastProgram.episode !== undefined) {
        // FIND FIRST EPISODE WITH A GREATER NUMBER
        const nextEpisodeNumber = sortedSeason.sortedPrograms.find(
          sortedProgram => (sortedProgram.program.episode || 0) > (lastProgram.episode || 0)
        );

        if (nextEpisodeNumber) {
          // SELECT ALL EPISODE IN SEASON WITH SAME EPISODE NUMBER
          const nextEpisodePrograms = sortedSeason.sortedPrograms.filter(
            sortedProgram => sortedProgram.program.episode === nextEpisodeNumber.program.episode
          );

          // FIND A REPLAYABLE PROGRAM
          selectedNextProgram = nextEpisodePrograms.find(
            sortedProgram =>
              EpgUtil.canWatchReplay(sortedProgram.schedule, new ChannelModel(sortedProgram.channel)) ||
              this.hasWatchableRecording(sortedProgram, recordings)
          );
        }
      }

      // IF EPISODE OF LAST SEASON IS UNDEFINED, SELECT NEXT EPISODE IN ARRAY
      if (
        lastProgram.episode === undefined &&
        lastProgramIndex >= 0 &&
        lastProgramIndex + 1 < sortedSeason.sortedPrograms.length - 1
      ) {
        // CHECK IF IS REPLAYABLE
        const tempNextProgram = sortedSeason.sortedPrograms[lastProgramIndex + 1];
        if (tempNextProgram && tempNextProgram.channel) {
          selectedNextProgram = EpgUtil.canWatchReplay(
            tempNextProgram.schedule,
            new ChannelModel(tempNextProgram.channel)
          )
            ? tempNextProgram
            : undefined;
        }
      }
    }

    return selectedNextProgram;
  }

  hasWatchableRecording(program: ISortedProgram, recordings: Recording[]) {
    if (recordings && program.channel && program.schedule && program.program) {
      const bookmarkRec = recordings.find(
        recording =>
          recording.channel === program.channel.channelId &&
          recording.program === program.program.id &&
          recording.schedule === program.schedule.id
      );
      return bookmarkRec
        ? EpgUtil.canPlayRecording(this.backend, bookmarkRec, program.schedule, new ChannelModel(program.channel))
        : false;
    }

    return false;
  }

  getFirstEpiOfFirstSeason(
    sortedProgramsPerSeason: ISortedSeasons[],
    recordings: Recording[]
  ): ISortedProgram | undefined {
    if (sortedProgramsPerSeason && sortedProgramsPerSeason.length > 0) {
      const season = sortedProgramsPerSeason[0];

      if (season && season.sortedPrograms && season.sortedPrograms.length > 0) {
        const tempNextProgram = season.sortedPrograms[0];
        if (!tempNextProgram) return undefined;
        return EpgUtil.canWatchReplay(tempNextProgram.schedule, new ChannelModel(tempNextProgram.channel)) ||
          this.hasWatchableRecording(tempNextProgram, recordings)
          ? tempNextProgram
          : undefined;
      }
    }

    return undefined;
  }
}
